import { useMemo } from "react";
import { eachDayOfInterval, format, isSameDay, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { AlertTriangle } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useFetchShifts } from "@/hooks/useShifts";
import { useFetchWorkSessions } from "@/hooks/useWorkSessions";
import type { Shift } from "@/types/shift.type";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getShiftMinutes(shift: Shift): number {
  if (shift.status !== "PLANNED") return 0;
  const start = new Date(shift.startTime).getTime();
  const end = new Date(shift.endTime).getTime();
  const diffMin = Math.floor((end - start) / 60000);
  return Math.max(0, diffMin - (shift.breakMinutes ?? 0));
}

function formatDuration(minutes: number, withSign = false): string {
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const sign = withSign ? (minutes > 0 ? "+" : minutes < 0 ? "−" : "") : "";
  return m === 0 ? `${sign}${h}h` : `${sign}${h}h${String(m).padStart(2, "0")}`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OvertimeDialogEmployee {
  id: string;
  name: string;
}

interface Props {
  employee: OvertimeDialogEmployee | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Date ISO "yyyy-MM-dd" du premier jour de la période */
  from: string;
  /** Date ISO "yyyy-MM-dd" du dernier jour de la période (inclus) */
  to: string;
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const EmployeeOvertimeDialog = ({
  employee,
  open,
  onOpenChange,
  from,
  to,
}: Props) => {
  const { data: shiftsData, isLoading: shiftsLoading } = useFetchShifts(
    { from, to, employeeId: employee?.id },
    { enabled: open && !!employee?.id }
  );

  const { data: sessionsData, isLoading: sessionsLoading } = useFetchWorkSessions(
    { userId: employee?.id, startDate: from, endDate: to, limit: 200 },
    { enabled: open && !!employee?.id }
  );

  const isLoading = shiftsLoading || sessionsLoading;
  const shifts = shiftsData?.shifts ?? [];
  const sessions = sessionsData?.workSessions ?? [];

  const shiftsByDay = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const s of shifts) {
      const key = s.workDate
        ? format(parseISO(s.workDate), "yyyy-MM-dd")
        : format(new Date(s.startTime), "yyyy-MM-dd");
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    return map;
  }, [shifts]);

  const sessionsByDay = useMemo(() => {
    const map = new Map<string, typeof sessions>();
    for (const s of sessions) {
      const startDate = new Date(s.startTime);
      // Shift de nuit : si la session commence entre 00:00 et 05:59,
      // on la rattache au jour précédent pour correspondre au workDate du shift.
      const isNightCarryover = startDate.getHours() < 6;
      let key: string;
      if (isNightCarryover) {
        const prev = new Date(startDate);
        prev.setDate(prev.getDate() - 1);
        key = format(prev, "yyyy-MM-dd");
      } else {
        key = s.date
          ? format(parseISO(s.date), "yyyy-MM-dd")
          : format(startDate, "yyyy-MM-dd");
      }
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    return map;
  }, [sessions]);

  const rows = useMemo(() => {
    if (!from || !to) return [];
    const days = eachDayOfInterval({ start: parseISO(from), end: parseISO(to) });
    return days.map((day) => {
      const dayKey = format(day, "yyyy-MM-dd");
      const dayShifts = shiftsByDay.get(dayKey) ?? [];
      const daySessions = sessionsByDay.get(dayKey) ?? [];

      const plannedMinutes = dayShifts.reduce(
        (acc, s) => acc + getShiftMinutes(s),
        0
      );
      const workedMs = daySessions.reduce((acc, s) => {
        if (!s.endTime) return acc;
        return acc + (new Date(s.endTime).getTime() - new Date(s.startTime).getTime());
      }, 0);
      const workedMinutes = Math.round(workedMs / 60000);

      const overtimeMinutes = workedMinutes - plannedMinutes;
      const hasPlanned = plannedMinutes > 0;
      const hasWorked = workedMinutes > 0;

      return {
        day,
        dayKey,
        plannedMinutes,
        workedMinutes,
        overtimeMinutes,
        hasOvertime: overtimeMinutes > 0 && hasPlanned,
        hasUndertime: overtimeMinutes < 0 && hasPlanned,
        hasActivity: hasPlanned || hasWorked,
      };
    });
  }, [from, to, shiftsByDay, sessionsByDay]);

  // Total calculé depuis toutes les sessions en une seule conversion ms→min
  // (pas la somme des arrondis par jour) pour correspondre exactement au backend.
  const totalWorkedMs = sessions.reduce((acc, s) => {
    if (!s.endTime) return acc;
    return acc + (new Date(s.endTime).getTime() - new Date(s.startTime).getTime());
  }, 0);
  const totalWorkedMinutes = Math.round(totalWorkedMs / 60000);
  const totalPlannedMinutes = rows.reduce((acc, r) => acc + r.plannedMinutes, 0);
  const totalOvertimeMinutes = totalWorkedMinutes - totalPlannedMinutes;

  if (!employee) return null;

  const fromDate = parseISO(from);
  const toDate = parseISO(to);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Dépassements — {employee.name}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Du {format(fromDate, "d MMM", { locale: fr })} au{" "}
            {format(toDate, "d MMM yyyy", { locale: fr })}
          </p>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0">
          {isLoading ? (
            <div className="space-y-2 pt-1">
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded" />
              ))}
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border mt-1">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-muted/80 border-b backdrop-blur-sm">
                    <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Jour
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Planifié
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Pointé
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Écart
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isToday = isSameDay(row.day, new Date());
                    return (
                      <tr
                        key={row.dayKey}
                        className={[
                          "border-b last:border-b-0 transition-colors",
                          !row.hasActivity ? "opacity-40" : "",
                          row.hasOvertime ? "bg-red-50/50" : "",
                          isToday ? "font-semibold" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <td className="px-3 py-2.5 capitalize">
                          {format(row.day, "EEE d MMM", { locale: fr })}
                          {isToday && (
                            <span className="ml-1.5 text-[10px] text-primary font-bold uppercase">
                              Auj.
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                          {row.plannedMinutes > 0
                            ? formatDuration(row.plannedMinutes)
                            : "—"}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {row.workedMinutes > 0
                            ? formatDuration(row.workedMinutes)
                            : "—"}
                        </td>
                        <td
                          className={[
                            "px-3 py-2.5 text-right tabular-nums font-semibold",
                            row.hasOvertime
                              ? "text-red-600"
                              : row.hasUndertime
                                ? "text-orange-500"
                                : row.hasActivity
                                  ? "text-emerald-600"
                                  : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {row.hasActivity
                            ? formatDuration(row.overtimeMinutes, true)
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="sticky bottom-0">
                  <tr className="bg-muted/80 border-t font-semibold backdrop-blur-sm">
                    <td
                      className="px-3 py-2.5 text-sm text-muted-foreground"
                      colSpan={3}
                    >
                      Total de la période
                    </td>
                    <td
                      className={[
                        "px-3 py-2.5 text-right tabular-nums text-sm",
                        totalOvertimeMinutes > 0
                          ? "text-red-600"
                          : totalOvertimeMinutes < 0
                            ? "text-orange-500"
                            : "text-emerald-600",
                      ].join(" ")}
                    >
                      {formatDuration(totalOvertimeMinutes, true)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
